import { Menu, ActionIcon, MantineColor, Tooltip } from '@mantine/core';
import { 
  IconDots, 
  IconUpload, 
  IconDownload, 
  IconSettings,
  IconTrash,
  IconEdit
} from '@tabler/icons-react';
import { useState, useMemo } from 'react';
import React from 'react';

/** Variantes visuales customizadas para el trigger del menu */
export type AfaMenuVariant = 'filled' | 'light' | 'disabled' | 'outline' | 'alert' | 'static';

export interface AfaMenuProps {
  /** Array de elementos del menú */
  items?: AfaMenuItemProps[];
  /** Variante visual customizada del trigger */
  variant?: AfaMenuVariant;
  /** Tamaño del icono del trigger */
  triggerSize?: number;
  /** Icono del trigger */
  triggerIcon?: React.ReactNode;
  /** Color del trigger */
  triggerColor?: MantineColor;
  /** Radio del trigger (xs, sm, md, lg, xl o número) */
  triggerRadius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  /** Ancho del dropdown */
  width?: number;
  /** Sombra del dropdown */
  shadow?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Si el menu está deshabilitado */
  disabled?: boolean;
  /** Controla si el clic abre el menu (por defecto true) */
  clickToOpen?: boolean;
  /** Callback cuando el menu se abre */
  onOpen?: () => void;
  /** Callback cuando el menu se cierra */
  onClose?: () => void;
  /** Props adicionales para el Menu */
  menuProps?: any;
  /** Props adicionales para el ActionIcon trigger */
  triggerProps?: any;
  /** Texto del tooltip (solo para variante static) */
  tooltip?: string;
  /** Posición del tooltip */
  tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
}

export interface AfaMenuItemProps {
  /** Texto del elemento */
  label: string;
  /** Icono a la izquierda */
  icon?: React.ReactNode;
  /** Color del elemento (para elementos peligrosos) */
  color?: string;
  /** Función a ejecutar al hacer clic */
  onClick?: () => void;
  /** Si el elemento está deshabilitado */
  disabled?: boolean;
  /** Si debe mostrar un divisor después de este elemento */
  divider?: boolean;
}

/**
 * Obtiene las propiedades del trigger basadas en la variante
 * Todas las variantes están completamente definidas aquí con sus colores y propiedades
 * 
 * @param variant - Variante visual del trigger
 * @param disabled - Si el trigger está deshabilitado
 * @param triggerColor - Color customizado del trigger (opcional, override de la variante)
 * @param triggerRadius - Radio customizado del trigger
 * @returns Objeto con las props del ActionIcon
 */
function getTriggerProps(
  variant: AfaMenuVariant, 
  disabled: boolean, 
  triggerColor?: MantineColor,
  triggerRadius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number
) {
  const baseProps = {
    disabled,
    radius: triggerRadius || 'md', // Radio por defecto
  };

  switch (variant) {
    case 'filled':
      return {
        ...baseProps,
        variant: 'filled' as const,
        color: triggerColor || 'brand', // Primary/themePrimary (#00476B)
        size: 'md' as const,
      };
    
    case 'light':
      return {
        ...baseProps,
        variant: 'light' as const,
        color: triggerColor || 'brand', // Misma color que AfaButton light
        size: 'md' as const,
      };
    
    case 'disabled':
      return {
        ...baseProps,
        variant: 'subtle' as const,
        color: 'gray', // Siempre gris para disabled
        disabled: true,
        size: 'md' as const,
      };
    
    case 'outline':
      return {
        ...baseProps,
        variant: 'outline' as const,
        color: triggerColor || 'brand', // Borde en color primario
        size: 'md' as const,
      };
    
    case 'alert':
      return {
        ...baseProps,
        variant: 'filled' as const,
        color: triggerColor || 'red',
        size: 'md' as const,
      };
    
    case 'static':
      // Mantine no reconoce 'static', entonces usamos 'filled' para garantizar el visual igual
      return {
        ...baseProps,
        variant: 'filled' as const,
        color: triggerColor || 'brand',
        size: 'md' as const,
      };
    
    default:
      return {
        ...baseProps,
        variant: 'subtle' as const,
        color: triggerColor || 'gray',
        size: 'md' as const,
      };
  }
}

/**
 * Obtiene los elementos del menú por defecto basados en la variante
 */
function getDefaultItemsForVariant(variant: AfaMenuVariant): AfaMenuItemProps[] {
  switch (variant) {
    case 'filled':
      // Menú para acciones primarias importantes
      return [
        {
          label: 'Editar',
          icon: <IconEdit size={14} />
        },
        {
          label: 'Duplicar',
          icon: <IconUpload size={14} />
        },
        {
          label: 'Descargar',
          icon: <IconDownload size={14} />,
          divider: true
        },
        {
          label: 'Eliminar',
          icon: <IconTrash size={14} />,
          color: 'red'
        }
      ];
    
    case 'light':
      // Menú estándar con acciones comunes
      return [
        {
          label: 'Ver detalles',
          icon: <IconSettings size={14} />
        },
        {
          label: 'Compartir',
          icon: <IconUpload size={14} />
        },
        {
          label: 'Descargar',
          icon: <IconDownload size={14} />
        }
      ];
    
    case 'outline':
      // Menú de configuración
      return [
        {
          label: 'Configuración',
          icon: <IconSettings size={14} />
        },
        {
          label: 'Exportar',
          icon: <IconUpload size={14} />
        },
        {
          label: 'Importar',
          icon: <IconDownload size={14} />
        }
      ];
    
    case 'alert':
      // Sin menú - solo alerta visual
      return [];
    
    case 'disabled':
      // Sin menú - está deshabilitado
      return [];
    
    default:
      return [
        {
          label: 'Acción genérica',
          icon: <IconSettings size={14} />
        }
      ];
  }
}

export function AfaMenu({
  items = [],
  variant = 'light',
  triggerSize = 16,
  triggerIcon,
  triggerColor,
  triggerRadius = 'md',
  width = 200,
  shadow = 'md',
  disabled = false,
  clickToOpen = true,
  onOpen,
  onClose,
  menuProps,
  triggerProps,
  tooltip,
  tooltipPosition = 'top',
  ...rest
}: AfaMenuProps) {
  const [opened, setOpened] = useState(false);
  
  // Elementos por defecto basados en la variante si no se proporcionan - memoizado
  const defaultItemsForVariant = useMemo(() => getDefaultItemsForVariant(variant), [variant]);
  const menuItems = items.length > 0 ? items : defaultItemsForVariant;
  
  // Props del trigger basadas en la variante - memoizado
  const computedTriggerProps = useMemo(
    () => getTriggerProps(variant, disabled, triggerColor, triggerRadius),
    [variant, disabled, triggerColor, triggerRadius]
  );
  
  // Ícono del trigger: usa la prop triggerIcon o un ícono por defecto según la variante - memoizado
  const finalTriggerIcon = useMemo(() => {
    if (triggerIcon) return triggerIcon;
    
    if (variant === 'alert') {
      return <IconTrash size={triggerSize} style={{ color: 'var(--mantine-color-red-8)' }} />;
    }
    return <IconDots size={triggerSize} />;
  }, [triggerIcon, variant, triggerSize]);

  // Ícono para variante static - siempre clona el icono y fuerza size/stroke - memoizado
  const staticIcon = useMemo(() => {
    if (triggerIcon && React.isValidElement(triggerIcon)) {
      return React.cloneElement(triggerIcon as React.ReactElement<any>, { size: triggerSize, stroke: 1.5 });
    }
    return <IconDots size={triggerSize} stroke={1.5} />;
  }, [triggerIcon, triggerSize]);

  // Variante "static": solo muestra el icono, no abre menú
  if (variant === 'static') {
    const actionIconElement = (
      <ActionIcon
        {...computedTriggerProps}
        {...triggerProps}
        aria-label="Acción estática"
        data-variant="static"
      >
        {staticIcon}
      </ActionIcon>
    );

    // Si tiene tooltip, envuelve el ActionIcon con Tooltip
    if (tooltip) {
      return (
        <Tooltip label={tooltip} position={tooltipPosition} withArrow>
          {actionIconElement}
        </Tooltip>
      );
    }

    return actionIconElement;
  }

  const handleToggle = () => {
    // Variantes que nunca abren el menú
    if (variant === 'alert' || variant === 'disabled') {
      // Para variant="alert", ejecutar el onClick del triggerProps si existe
      if (variant === 'alert' && triggerProps?.onClick) {
        triggerProps.onClick();
      }
      return;
    }
    
    if (!clickToOpen || disabled) return;
    
    const newOpened = !opened;
    setOpened(newOpened);
    
    if (newOpened && onOpen) {
      onOpen();
    } else if (!newOpened && onClose) {
      onClose();
    }
  };

  return (
    <Menu 
      shadow={shadow} 
      width={width}
      opened={opened}
      onClose={() => {
        setOpened(false);
        if (onClose) onClose();
      }}
      {...menuProps}
      {...rest}
    >
      <Menu.Target>
        {variant === 'alert' ? (
          <Tooltip label="Esborrar" position="top" withArrow>
            <ActionIcon 
              {...computedTriggerProps}
              {...triggerProps}
              onClick={handleToggle}
              aria-label="Esborrar"
              style={{
                backgroundColor: 'var(--mantine-color-red-0)',
                color: 'var(--mantine-color-red-8)',
                border: 'none',
                ...triggerProps?.style
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--mantine-color-red-1)';
                triggerProps?.onMouseEnter?.(e);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--mantine-color-red-0)';
                triggerProps?.onMouseLeave?.(e);
              }}
            >
              {finalTriggerIcon}
            </ActionIcon>
          </Tooltip>
        ) : (
          <ActionIcon 
            {...computedTriggerProps}
            {...triggerProps}
            onClick={handleToggle}
            aria-label="Más acciones"
            style={triggerProps?.style}
          >
            {finalTriggerIcon}
          </ActionIcon>
        )}
      </Menu.Target>

      <Menu.Dropdown>
        {menuItems.map((item, index) => {
          let leftSection = item.icon;
          if (React.isValidElement(leftSection)) {
            leftSection = React.cloneElement(leftSection as React.ReactElement<any>, { size: 14 });
          }
          return (
            <div key={index}>
              <Menu.Item
                leftSection={leftSection}
                {...(item.color && { color: item.color })}
                {...(typeof item.disabled === 'boolean' && { disabled: item.disabled })}
                onClick={item.onClick}
              >
                {item.label}
              </Menu.Item>
              {item.divider && <Menu.Divider />}
            </div>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

export default AfaMenu;
